import { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { FaCloudArrowDown } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { FaArrowAltCircleDown, FaSpinner } from 'react-icons/fa';
import * as api from '../../../../services/api';
import Pagination from '../../../Pricing/Pagination';
import Table from '../../../../components/Table';
import {
  IoIosArrowDown,
  IoIosArrowForward,
  IoIosCheckmarkCircleOutline,
  IoMdCloseCircleOutline,
} from 'react-icons/io';
import { DefaultMessage } from '../../../../utils/defaultMessages';
import { formatDateTime } from '../../../../utils/FormatDateTime/FormatDateTime';
import DownloadXLSX from '../../../../utils/DownloadXLSX/DownloadXLSX';
import { motion } from 'framer-motion';

interface TableRowData {
  id: string;
  name: string;
  task: string;
  queue: string;
  product: string;
  priority: number | null | string;
  start_time: string;
  last_run_at: string;
  total_run_count: number;
  date_changed: string;
  description: string;
  interval: null | number;
  crontab: null | number;
  enabled: boolean | string;
  type_agendamento: string;
}

export default function Upload() {
  const [progress, setProgress] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<string>('');
  // const [functionalityOption, setFunctionalityOption] = useState<string>('');
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [tableData, setTableData] = useState<TableRowData[]>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [optionsState, setOptionsState] = useState<boolean>(false);
  const [isClosingStatus, setIsClosingStatus] = useState(false);

  const columns = [
    {
      Header: 'Id',
      accessor: 'id',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Produto',
      accessor: 'product',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Nome do arquivo',
      accessor: 'file_name',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Criado por',
      accessor: 'created_by_user_email',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Criado em',
      accessor: 'created_at',
      Cell: ({value}: any) => (value === null ? '-' : formatDateTime(value)),
    },
    {
      Header: 'Atualizado em',
      accessor: 'updated_at',
      Cell: ({value}: any) => (value === null ? '-' : formatDateTime(value)),
    },
    {
      Header: 'Status',
      accessor: 'status_detail',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Motivo do erro',
      accessor: 'error_detail',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Ativo?',
      accessor: 'is_active',
      Cell: ({value}: any) => (
        <div className="flex justify-center">
          {value ? (
            <IoIosCheckmarkCircleOutline size={20} className="text-[#00AEEF]" />
          ) : (
            <IoMdCloseCircleOutline size={20} className="text-red-400" />
          )}
        </div>
      ),
    },
    {
      Header: 'Total de linhas',
      accessor: 'total_lines',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Linhas processadas',
      accessor: 'processed_lines',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Linhas processadas com erro',
      accessor: 'processed_lines_error',
      Cell: ({value}: any) => (value === null ? '-' : value),
    },
    {
      Header: 'Exportar',
      accessor: 'worksheet_id',
      Cell: ({value}: any) => (
        <div
          className="flex justify-center hover:cursor-pointer"
          onClick={() => handleExpot(value)}
        >
          <FaArrowAltCircleDown size={20} className="text-[#00aeef]" />
        </div>
      ),
    },
  ];

  const machines = [
    { id: '4', name: 'CARROCERIAS' },
    { id: '6', name: 'COLHEITADEIRA' },
    // { id: "2", name: "Helicóptero" },
    { id: '3', name: 'SEMIRREBOQUE' },
    { id: '1', name: 'TRATOR' },
  ];

  // const functionality = [
  //     { id: "1", name: "REGISTRAR DADOS" },
  //     { id: "2", name: "ATUALIZAR DADOS" },
  // ];

  const handleMachineChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMachine(event.target.value);
  };
  // const handleFunctionalityOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //     setFunctionalityOption(event.target.value);
  // };

  const fileChanger = (file: File) => {
    if (
      file.type !==
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      toast.error(
        'Formato de arquivo inválido. Por favor, envie um arquivo .xlsx.',
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
      return;
    }
    setFileToUpload(file);
  };

  const handleUpload = () => {
    localStorage.clear();

    if (!fileToUpload) {
      toast.warn('Nenhum arquivo selecionado.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!selectedMachine) {
      toast.warn('Nenhum produto selecionado.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const rota = `/table/crawler/create/manual/${selectedMachine}/`;

    setProgress(true);
    setIsUploading(true);

    uploadFile(fileToUpload, rota)
      .then((response) => {
        if (response.status === 200 || response.status === 202) {
          toast.success('Planilha processada com sucesso!', {
            position: toast.POSITION.TOP_RIGHT,
          });
          updateTable();
        } else if (response.status === 400) {
          toast.error('Arquivo incorreto!', {
            position: toast.POSITION.TOP_RIGHT,
          });
          if (response.data?.content) {
            toast.error(response.data.content, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        } else {
          toast.error('Erro no upload. Tente novamente!', {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error('Erro no upload. Tente novamente!', {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setProgress(false);
        setIsUploading(false);
        setFileToUpload(null);
      });
  };

  const uploadFile = async (file: File, rota: string) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response: any = await api.post(rota, formData);

      return response;
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      throw error;
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    updateTable(newPage);
  };

  const handleExpot = async (id: number) => {
    DownloadXLSX(`/crawler/xlsx/download/monitor/?worksheet_id=${id}`);
  };

  useEffect(() => {
    if (optionsState) {
      updateTable();
    }
  }, [optionsState]);

  const updateTable = async (currentPage: number = 1) => {
    try {
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      const formattedStartDate = startDate.toISOString().split('T')[0];

      const endDate = new Date().toISOString().split('T')[0];

      const url = `/crawler/manage_crawler/xlsx/list/?start_date=${formattedStartDate}&end_date=${endDate}&page=${currentPage}`;

      const response: any = await api.get(url);
      if (response.status === 200) {
        setTableData(response.data.content.results);
        setTotalPages(response.data.content.pages);
        setCurrentPage(currentPage);
      }
    } catch {
      toast.error(DefaultMessage.ERROR, { position: toast.POSITION.TOP_RIGHT });
    }
  };

  const handleStatus = () => {
    if (optionsState) {
      setIsClosingStatus(true);
      setTimeout(() => {
        setOptionsState(false);
        setIsClosingStatus(false);
      }, 500);
    } else {
      setOptionsState(true);
    }
  };

  return (
    <>
      <div className="flex p-4 bg-white rounded-b-xl">
        <div className="w-full fle justify-center">
          <div className="flex items-start gap-6 md:items-end flex-col justify-center md:flex-row md:justify-between">
            <div className=" w-[100%] sm:w-[50%]">
              <FileUploader
                className="w-screen"
                progress={progress}
                hoverTitle={
                  <h5 className="w-[100%] text-black text-center bg-[#6B7280]">
                    <FaCloudArrowDown /> Tudo certo! Pode soltar :)
                  </h5>
                }
                // onDraggingStateChange={handleDraggingStateChange}
                multiple={false}
                handleChange={fileChanger}
                name={'file'}
                types={['XLSX']}
              >
                <div className="w-[21.3rem] md:w-[25rem] lg:w-[29rem] xl:w-[35rem] 2xl:w-[45rem] h-[3.8rem] p-[1rem] border-2 border-dashed border-[#808080] flex justify-center items-center text-[#808080] text-center rounded-full">
                  {fileToUpload ? (
                    <span>Arquivo selecionado: {fileToUpload.name}</span>
                  ) : (
                    <span className="flex gap-4 items-center">
                      <FaCloudArrowDown size={25} /> Arraste seu arquivo aqui!
                    </span>
                  )}
                </div>
              </FileUploader>
            </div>
            <div className="w-[21.3rem] md:w-[25rem] flex flex-col gap-4 md:flex-row">
              {/* <label htmlFor="machine-select" className="block text-base font-bold text-[#6B7280]">
                                PRODUTO
                            </label> */}
              <select
                id="machine-select"
                value={selectedMachine}
                onChange={handleMachineChange}
                className="bg-transparent mt-1 block w-full h-[3.8rem] border-2 border-gray-200 text-[#6B7280] font-semibold px-6 py-[1rem] rounded-full shadow-sm focus:outline-none focus:ring-gray-200 focus:border-gray-200 sm:text-sm"
              >
                <option value="" disabled selected>
                  PRODUTO
                </option>
                {machines.map((machine) => (
                  <option
                    className="text-[#6B7280] font-semibold"
                    key={machine.id}
                    value={machine.id}
                  >
                    {machine.name}
                  </option>
                ))}
              </select>
              {/* <select
                                id="functionality-select"
                                value={functionalityOption}
                                onChange={handleFunctionalityOption}
                                className="bg-transparent mt-1 block w-full h-[3.8rem] border-2 border-gray-200 text-[#6B7280] font-semibold pr-6 py-[1rem] rounded-full shadow-sm focus:outline-none focus:ring-gray-200 focus:border-gray-200 sm:text-sm"
                            >
                                <option value="" disabled selected>
                                    SELECIONE UMA AÇÃO
                                </option>
                                {functionality.map((functionality) => (
                                    <option className="text-[#6B7280] font-semibold" key={functionality.id} value={functionality.id}>
                                        {functionality.name}
                                    </option>
                                ))}
                            </select> */}
            </div>
          </div>
          <div className="w-[100%] flex justify-center">
            <button
              className="bg-[#00AEEF] text-white h-[3.8rem] sm:w-[43%] rounded-full w-full mt-4 md:h-[3.2rem] md:w-[21.3rem]"
              type="button"
              disabled={isUploading}
              onClick={handleUpload}
            >
              {isUploading ? (
                <div className="flex justify-center items-center">
                  <FaSpinner
                    className="animate-spin text-white mr-2"
                    size={30}
                  />
                  <span>Carregando</span>
                </div>
              ) : (
                'Carregar planilha'
              )}
            </button>
          </div>
          <div className="flex items-center mt-16" onClick={handleStatus}>
            {optionsState ? <IoIosArrowDown /> : <IoIosArrowForward />}
            <button className="w-max font-semibold text-gray-600 px-4 py-2 rounded-full z-0">
              STATUS DE PROCESSAMENTO
            </button>
          </div>
          <motion.div
            className=" transition-all duration-500"
            // layout
          >
            {optionsState && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{
                  opacity: isClosingStatus ? 0 : 1,
                  y: isClosingStatus ? -20 : 0,
                  zIndex: isClosingStatus ? -1 : 0,
                }}
                transition={{ duration: 0.5 }}
              >
                <Table columns={columns} data={tableData} />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
